import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight } from "lucide-react";

const STEPS = [
  { id: "personal", title: "Personal" },
  { id: "body", title: "Body" },
  { id: "running", title: "Running" },
  { id: "lifestyle", title: "Lifestyle" },
];

export default function Onboarding() {
  const { refresh, user } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const initialGoal = (typeof localStorage !== "undefined") ? localStorage.getItem("coachjp_goal") : null;
  const [data, setData] = useState({
    age: "", sex: "M", phone: "", weight_kg: "", height_cm: "",
    running_level: "intermediate", weekly_mileage_km: "", pb_5k: "", pb_10k: "", pb_half: "", pb_marathon: "",
    goal_race: initialGoal || (user?.goal || "5k"), goal_time: "", target_race_date: "", weekly_availability_days: 4,
    recovery_habits: [], injuries: "",
    running_experience_years: "1_3", cross_training: [], training_surface: "road",
    races_last_year: "",
  });

  const update = (k, v) => setData(d => ({ ...d, [k]: v }));

  const submit = async () => {
    const payload = { ...data };
    ["age","weight_kg","height_cm","weekly_mileage_km","weekly_availability_days","races_last_year"].forEach(k => {
      if (payload[k] !== "" && payload[k] !== null && payload[k] !== undefined) payload[k] = Number(payload[k]);
      if (Number.isNaN(payload[k])) delete payload[k];
    });
    try {
      // also set goal
      await api.post("/profile/goal", { goal: payload.goal_race });
      await api.post("/profile", payload);
      await refresh();
      toast.success("Profile saved — let's train");
      nav("/app/dashboard");
    } catch (e) { toast.error("Could not save profile"); }
  };

  return (
    <div className="min-h-screen bg-[#070707] text-white" data-testid="onboarding-page">
      <div className="max-w-2xl mx-auto px-6 py-10">
        <div className="flex items-center gap-3">
          {STEPS.map((s, i) => (
            <div key={s.id} className={`flex-1 h-1 ${i <= step ? "bg-[#d7ff00]" : "bg-[#222]"}`} />
          ))}
        </div>
        <div className="mt-4 font-mono-jp text-[10px] uppercase tracking-widest text-[#888]">STEP {step + 1} / {STEPS.length} · {STEPS[step].title.toUpperCase()}</div>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl uppercase">Build your <span className="text-[#d7ff00]">profile</span></h1>

        <div className="mt-8 space-y-4">
          {step === 0 && (
            <>
              <Field label="Email (locked)">
                <input value={user?.email || ""} disabled className={`${inputCls} opacity-70 cursor-not-allowed`} data-testid="ob-email" />
              </Field>
              <Field label="Age" required><input type="number" required value={data.age} onChange={e => update("age", e.target.value)} className={inputCls} data-testid="ob-age" /></Field>
              <Field label="Sex" required>
                <select value={data.sex} onChange={e => update("sex", e.target.value)} className={inputCls} data-testid="ob-sex">
                  <option value="M">Male</option><option value="F">Female</option><option value="O">Other</option>
                </select>
              </Field>
              <Field label="Phone number" required>
                <input type="tel" required value={data.phone} onChange={e => update("phone", e.target.value)} placeholder="+60 12 345 6789" className={inputCls} data-testid="ob-phone" />
              </Field>
            </>
          )}
          {step === 1 && (
            <>
              <Field label="Weight (kg)" required><input type="number" value={data.weight_kg} onChange={e => update("weight_kg", e.target.value)} className={inputCls} data-testid="ob-weight" /></Field>
              <Field label="Height (cm)" required><input type="number" value={data.height_cm} onChange={e => update("height_cm", e.target.value)} className={inputCls} data-testid="ob-height" /></Field>
              <Field label="Injury history (optional)"><textarea rows={3} value={data.injuries} onChange={e => update("injuries", e.target.value)} className={inputCls} data-testid="ob-injuries" /></Field>
            </>
          )}
          {step === 2 && (
            <>
              <Field label="Running level" required>
                <select value={data.running_level} onChange={e => update("running_level", e.target.value)} className={inputCls} data-testid="ob-level">
                  <option value="beginner">Beginner</option><option value="intermediate">Intermediate</option><option value="advanced">Advanced</option><option value="elite">Elite</option>
                </select>
              </Field>
              <Field label="Weekly mileage (km)" required><input type="number" value={data.weekly_mileage_km} onChange={e => update("weekly_mileage_km", e.target.value)} className={inputCls} data-testid="ob-mileage" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="5K PB"><input value={data.pb_5k} onChange={e => update("pb_5k", e.target.value)} placeholder="mm:ss" className={inputCls} data-testid="ob-pb5k" /></Field>
                <Field label="10K PB"><input value={data.pb_10k} onChange={e => update("pb_10k", e.target.value)} placeholder="mm:ss" className={inputCls} data-testid="ob-pb10k" /></Field>
                <Field label="Half PB"><input value={data.pb_half} onChange={e => update("pb_half", e.target.value)} placeholder="h:mm:ss" className={inputCls} data-testid="ob-pbhalf" /></Field>
                <Field label="Marathon PB"><input value={data.pb_marathon} onChange={e => update("pb_marathon", e.target.value)} placeholder="h:mm:ss" className={inputCls} data-testid="ob-pbmarathon" /></Field>
              </div>
              <Field label="Goal race" required>
                <select value={data.goal_race} onChange={e => update("goal_race", e.target.value)} className={inputCls} data-testid="ob-goal-race">
                  <option value="beginner">Beginner</option>
                  <option value="5k">5K</option><option value="10k">10K</option>
                  <option value="half">Half Marathon</option><option value="marathon">Marathon</option>
                </select>
              </Field>
              <Field label="Goal time" required><input value={data.goal_time} onChange={e => update("goal_time", e.target.value)} placeholder="e.g. sub-20" className={inputCls} data-testid="ob-goal-time" /></Field>
              <Field label="Target race date" required><input type="date" value={data.target_race_date} onChange={e => update("target_race_date", e.target.value)} className={inputCls} data-testid="ob-race-date" /></Field>
            </>
          )}
          {step === 3 && (
            <>
              <Field label={`Weekly training availability: ${data.weekly_availability_days} days`}>
                <input type="range" min={2} max={7} value={data.weekly_availability_days} onChange={e => update("weekly_availability_days", e.target.value)} className="w-full accent-[#d7ff00]" data-testid="ob-availability" />
              </Field>
              <Field label="Years of running experience" required>
                <select value={data.running_experience_years} onChange={e => update("running_experience_years", e.target.value)} className={inputCls} data-testid="ob-experience">
                  <option value="under_1">Less than 1 year</option>
                  <option value="1_3">1 – 3 years</option>
                  <option value="3_5">3 – 5 years</option>
                  <option value="5_10">5 – 10 years</option>
                  <option value="over_10">10+ years</option>
                </select>
              </Field>
              <Field label="Cross-training activities (tap to toggle)">
                <div className="mt-2 flex flex-wrap gap-2" data-testid="ob-cross-training">
                  {["Cycling","Swimming","Gym/Strength","Yoga/Pilates","Hiking","Other"].map(opt => {
                    const active = (data.cross_training || []).includes(opt);
                    return (
                      <button
                        type="button"
                        key={opt}
                        onClick={() => {
                          const cur = data.cross_training || [];
                          update("cross_training", active ? cur.filter(x => x !== opt) : [...cur, opt]);
                        }}
                        className={`px-3 py-2 text-xs font-mono-jp uppercase tracking-widest border ${active ? "bg-[#d7ff00] text-black border-[#d7ff00]" : "bg-[#0e0e0e] text-white border-[#222] hover:border-[#d7ff00]/50"}`}
                        data-testid={`ob-cross-${opt.toLowerCase().replace(/[^a-z]/g,'')}`}
                      >
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </Field>
              <Field label="Typical training surface">
                <select value={data.training_surface} onChange={e => update("training_surface", e.target.value)} className={inputCls} data-testid="ob-surface">
                  <option value="road">Road</option>
                  <option value="trail">Trail</option>
                  <option value="track">Track</option>
                  <option value="treadmill">Treadmill</option>
                  <option value="mix">Mix</option>
                </select>
              </Field>
              <Field label="Races completed in the last 12 months">
                <input type="number" min={0} value={data.races_last_year} onChange={e => update("races_last_year", e.target.value)} placeholder="0" className={inputCls} data-testid="ob-races-last-year" />
              </Field>
            </>
          )}
        </div>

        <div className="mt-10 flex items-center gap-3">
          <button onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0} className="bg-[#111] border border-[#222] px-5 py-3 disabled:opacity-30 flex items-center gap-2" data-testid="ob-back">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          {step < STEPS.length - 1 ? (
            <button onClick={() => {
              if (step === 0) {
                if (!String(data.age).trim() || !String(data.phone).trim()) {
                  toast.error("Age and phone number are required");
                  return;
                }
              }
              if (step === 1) {
                if (!String(data.weight_kg).trim() || !String(data.height_cm).trim()) {
                  toast.error("Weight and height are required");
                  return;
                }
              }
              if (step === 2) {
                if (!String(data.weekly_mileage_km).trim() || !String(data.goal_time).trim() || !String(data.target_race_date).trim()) {
                  toast.error("Weekly mileage, goal time and race date are required");
                  return;
                }
              }
              setStep(s => s + 1);
            }} className="ml-auto bg-[#d7ff00] text-black font-display tracking-wider uppercase px-8 py-3 hover:bg-white flex items-center gap-2" data-testid="ob-next">
              Next <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button onClick={submit} className="ml-auto bg-[#d7ff00] text-black font-display tracking-wider uppercase px-8 py-3 hover:bg-white" data-testid="ob-submit">Enter the App</button>
          )}
        </div>
      </div>
    </div>
  );
}

const inputCls = "mt-2 w-full bg-[#0e0e0e] border border-[#222] focus:border-[#d7ff00] outline-none px-4 py-3 text-white";

function Field({ label, children, required }) {
  return (
    <div>
      <label className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888]">
        {label}{required && <span className="text-[#d7ff00] ml-1">*</span>}
      </label>
      {children}
    </div>
  );
}
